import { useState, useMemo } from 'react';
import type { TransactionRow } from '../../../hooks/useReports';

interface Props {
  transactions: TransactionRow[];
  onExportCSV: () => void;
}

const PAGE_SIZE = 10;

const STATUS_CLS: Record<string, string> = {
  pago: 'bg-emerald-100 text-emerald-700',
  pendente: 'bg-amber-100 text-amber-700',
  vencido: 'bg-red-100 text-red-600',
  atrasado: 'bg-red-100 text-red-600',
};

const RECURRENCE_PT: Record<string, string> = {
  monthly: 'Mensal', weekly: 'Semanal', annual: 'Anual', daily: 'Diário', biweekly: 'Quinzenal',
};

type SortKey = keyof Pick<TransactionRow, 'date' | 'amount' | 'description' | 'category' | 'status'>;

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDate = (d: string) => d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export const AnalyticsTable = ({ transactions, onExportCSV }: Props) => {
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortAsc, setSortAsc] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return transactions.filter(t =>
      !q || t.description.toLowerCase().includes(q) || t.category.toLowerCase().includes(q) || t.originName.toLowerCase().includes(q)
    );
  }, [transactions, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let va: any = a[sortKey];
      let vb: any = b[sortKey];
      if (sortKey === 'amount') { va = Number(va); vb = Number(vb); }
      if (va < vb) return sortAsc ? -1 : 1;
      if (va > vb) return sortAsc ? 1 : -1;
      return 0;
    });
  }, [filtered, sortKey, sortAsc]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageData = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(v => !v);
    else { setSortKey(key); setSortAsc(false); }
    setPage(1);
  };

  const SortIcon = ({ k }: { k: SortKey }) => (
    <i className={`text-[10px] ml-0.5 ${
      sortKey === k ? (sortAsc ? 'ri-arrow-up-line text-emerald-500' : 'ri-arrow-down-line text-emerald-500') : 'ri-expand-up-down-line text-gray-300'
    }`} />
  );

  return (
    <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3 flex-wrap">
        <div>
          <h3 className="text-[15px] font-700 text-gray-900">Tabela Analítica</h3>
          <p className="text-[11px] text-gray-400 mt-0.5">{filtered.length} lançamento{filtered.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <div className="relative">
            <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[12px]" />
            <input
              type="text"
              placeholder="Buscar..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="pl-7 pr-3 py-1.5 border border-gray-200 rounded-xl text-[12px] text-gray-700 focus:outline-none focus:border-emerald-400 w-44"
            />
          </div>
          <button
            onClick={onExportCSV}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[12px] font-600 rounded-xl cursor-pointer hover:bg-emerald-100 transition-colors whitespace-nowrap"
          >
            <i className="ri-download-2-line" />CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              {[
                { key: 'date' as SortKey, label: 'Data', w: 'w-28' },
                { key: 'description' as SortKey, label: 'Descrição', w: '' },
                { key: 'category' as SortKey, label: 'Categoria', w: 'w-32' },
                { key: null, label: 'Tipo', w: 'w-24' },
                { key: null, label: 'Origem', w: 'w-28' },
                { key: 'status' as SortKey, label: 'Status', w: 'w-24' },
                { key: null, label: 'Rec./Parc.', w: 'w-24' },
                { key: 'amount' as SortKey, label: 'Valor', w: 'w-28' },
              ].map(col => (
                <th
                  key={col.label}
                  onClick={() => col.key && toggleSort(col.key)}
                  className={`px-4 py-2.5 text-left text-[11px] font-600 text-gray-500 uppercase tracking-wider ${col.w} ${col.key ? 'cursor-pointer hover:text-gray-700' : ''}`}
                >
                  {col.label}
                  {col.key && <SortIcon k={col.key} />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {pageData.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-[13px] text-gray-400">
                  <i className="ri-file-list-line text-2xl text-gray-200 block mb-2" />
                  Nenhum lançamento encontrado
                </td>
              </tr>
            ) : pageData.map(t => (
              <tr key={t.id} className="hover:bg-gray-50/50 transition-colors">
                <td className="px-4 py-2.5 text-[11px] text-gray-500 whitespace-nowrap">{fmtDate(t.date)}</td>
                <td className="px-4 py-2.5 text-[12px] text-gray-800 font-500 max-w-[180px]">
                  <span className="truncate block">{t.description}</span>
                </td>
                <td className="px-4 py-2.5 text-[11px] text-gray-600 whitespace-nowrap">{t.category}</td>
                <td className="px-4 py-2.5">
                  <span className={`text-[10px] font-600 px-2 py-0.5 rounded-full ${
                    t.type === 'receita' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'
                  }`}>
                    {t.type === 'receita' ? 'Receita' : 'Despesa'}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-[11px] text-gray-600">
                  <div className="flex items-center gap-1">
                    <i className={`${t.origin === 'cartao' ? 'ri-bank-card-line' : 'ri-bank-line'} text-[11px] text-gray-400`} />
                    <span className="truncate max-w-[80px]">{t.originName}</span>
                  </div>
                </td>
                <td className="px-4 py-2.5">
                  <span className={`text-[10px] font-600 px-2 py-0.5 rounded-full ${STATUS_CLS[t.status] ?? 'bg-gray-100 text-gray-500'}`}>
                    {t.status || '—'}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-[10px] text-gray-400">
                  {t.recurrence ? (
                    <span className="flex items-center gap-0.5"><i className="ri-repeat-line" />{RECURRENCE_PT[t.recurrence] ?? t.recurrence}</span>
                  ) : t.installments > 1 ? (
                    <span className="flex items-center gap-0.5"><i className="ri-price-tag-3-line" />{t.installment_number}/{t.installments}x</span>
                  ) : <span className="text-gray-300">—</span>}
                </td>
                <td className={`px-4 py-2.5 text-[13px] font-700 whitespace-nowrap text-right ${
                  t.type === 'receita' ? 'text-emerald-600' : 'text-gray-800'
                }`}>
                  {t.type === 'receita' ? '+' : '-'}{fmt(t.amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
          <span className="text-[11px] text-gray-400">
            {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, sorted.length)} de {sorted.length}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-[14px]" />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pg = totalPages <= 5 ? i + 1 : Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
              return (
                <button
                  key={pg}
                  onClick={() => setPage(pg)}
                  className={`w-7 h-7 flex items-center justify-center rounded-lg text-[12px] font-600 cursor-pointer ${
                    pg === page ? 'bg-emerald-500 text-white' : 'border border-gray-200 text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {pg}
                </button>
              );
            })}
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-[14px]" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
